import type { Client } from '@efdigital/ll-addon-data';
import { DataTypeSystem } from '@efdigital/ll-addon-data/data-types';
import type { FindRecordOptions } from '@warp-drive/core/types/request';
import { findRecord } from '@warp-drive/utilities/json-api';
import { query } from '@warp-drive/utilities/json-api';

import { jsonApiOptions } from '../../../lib/json-api.ts';
import { filterRegex, page, sort } from '../../json-api.ts';

/**
 * Finds a client by id
 * @param clientId - The id of the client to find
 * @returns JSON API request to find the client
 */
export function findClient(clientId: string) {
  return findRecord<Client>(DataTypeSystem.Client, clientId, {
    ...jsonApiOptions,
  } as FindRecordOptions);
}

export interface ClientQueryOptions {
  title?: string;
  subscription?: string;
  view?: string;
  domain?: string;
  connection?: string;
  username?: string;
  expires?: string;
  sortField?: string;
  offset?: number;
  limit?: number;
}

/**
 * Query clients with search filters
 * @param options - Query options for filtering, sorting, and pagination
 * @returns JSON API request to query clients
 */
export function queryClients(options: ClientQueryOptions = {}) {
  const {
    title = '',
    subscription = '',
    view = '',
    domain = '',
    connection = '',
    username = '',
    expires = '',
    sortField = 'title',
    offset = 0,
    limit = 30,
  } = options;

  const params: Record<string, string | number> = {
    ...page({ offset, limit }),
    ...sort(sortField),
  };

  // Text filters
  if (title) {
    Object.assign(params, filterRegex('filter[title]', title));
  }

  if (domain) {
    Object.assign(params, filterRegex('filter[domains]', domain));
  }

  if (connection) {
    Object.assign(params, filterRegex('filter[connections]', connection));
  }

  if (username) {
    Object.assign(
      params,
      filterRegex('filter[corporateUsers.username]', username)
    );
  }

  // Exact match filters
  if (subscription) {
    params['filter[subscription]'] = subscription;
  }

  if (view) {
    params['filter[view]'] = view;
  }

  // Expiry filters
  if (expires) {
    if (expires === 'past') {
      // Show expired clients: expires <= now
      params['filter[expires][$lte]'] = new Date().toISOString();
    } else if (expires === 'future') {
      // Show current clients: expires > now
      params['filter[expires][$gt]'] = new Date().toISOString();
    } else if (expires === 'days-7') {
      // Show clients expiring within 7 days: now < expires <= now+7days
      const sevenDaysFromNow = new Date();
      sevenDaysFromNow.setDate(sevenDaysFromNow.getDate() + 7);
      params['filter[expires][$gt]'] = new Date().toISOString();
      params['filter[expires][$lte]'] = sevenDaysFromNow.toISOString();
    }
  }

  return query<Client>(DataTypeSystem.Client, params, jsonApiOptions);
}
